import React from "react";
import {Chip, Typography} from "@mui/material";
import {Blue3, Grey6} from "../theme/styles";

// THE lifecycle status palette, matching cres-t5/src/util/statusPill. One definition per app, and
// the same definition in both, because a status must not mean one colour in the Data Catalog and
// another in Foundation -- the objects share a lifecycle and people move between the two screens.
//
// This replaced three hand-maintained copies in t1: ObjectRevisionTable, ObjectDetail, and a
// two-way ternary in FormDesignerPage that painted DRAFT amber and EVERYTHING ELSE the same blue --
// so a released form, a superseded one and a voided one were indistinguishable. That is the failure
// this exists to prevent, and it is the reason a palette belongs in one file rather than in three.
//
// FILLED, NOT OUTLINED. t1 drew an outlined pill and t5 a filled one; filled is the standard because
// it reads at a glance in a dense grid, which is where most statuses are seen.
//
// The colours carry meaning and are not arbitrary:
//   RELEASED    green  -- live and current
//   DRAFT       blue   -- being worked on, not yet live
//   DEPRECATED  amber  -- STILL LIVE. It resolves for anything already pointing at it and is only
//                         kept out of pickers, so it is not red. This is the one people get wrong.
//   SUPERSEDED  grey   -- frozen, replaced by a newer revision
//   CANCELLED   grey   -- frozen, abandoned before release
//   VOID        red    -- out of service. The terminal state, and the only red one.
//
// BEYOND THE REVISION LIFECYCLE. Other vocabularies paint from this same palette, so a colour means
// the same thing wherever it appears rather than each screen inventing its own scheme:
//   SUCCEEDED / DONE / ONLINE          green  -- finished well, or healthy right now
//   RUNNING / IN PROGRESS              blue   -- under way
//   QUEUED / NEVER_SEEN                grey   -- waiting, or nothing has happened yet
//   STALE / SILENT                     amber  -- live but wants attention, like DEPRECATED
//   FAILED / REVOKED                   red    -- the same weight as VOID
//
// NOT EVERY LABELLED THING IS A STATUS. A watchlist FINDING ("Column gone") names a kind of problem
// and a ModificationType names a kind of change; neither has a lifecycle, and colouring them from
// this palette would imply one. They keep their own treatment on purpose.
const PALETTE: Record<string, {bg: string; fg: string}> = {
    RELEASED:   {bg: "rgba(46,204,113,0.16)",  fg: "#1E9E5A"},
    DRAFT:      {bg: "rgba(20,152,216,0.14)",  fg: Blue3},
    SUPERSEDED: {bg: "rgba(123,130,145,0.16)", fg: Grey6},
    DEPRECATED: {bg: "rgba(241,196,15,0.20)",  fg: "#8A5B00"},
    VOID:       {bg: "rgba(231,76,60,0.14)",   fg: "#D64535"},
    CANCELLED:  {bg: "rgba(123,130,145,0.16)", fg: Grey6},
    ARCHIVED:   {bg: "rgba(123,130,145,0.16)", fg: Grey6},   // legacy rows, until they are migrated
    // Run outcomes -- flow runs, harvests. These were falling through to UNKNOWN, so a FAILED
    // harvest and a SUCCEEDED one were the same grey on the Source page.
    SUCCEEDED:  {bg: "rgba(46,204,113,0.16)",  fg: "#1E9E5A"},
    FAILED:     {bg: "rgba(231,76,60,0.14)",   fg: "#D64535"},
    RUNNING:    {bg: "rgba(20,152,216,0.14)",  fg: Blue3},
    QUEUED:     {bg: "rgba(123,130,145,0.16)", fg: Grey6},
    // Agent health.
    ONLINE:     {bg: "rgba(46,204,113,0.16)",  fg: "#1E9E5A"},
    SILENT:     {bg: "rgba(241,196,15,0.20)",  fg: "#8A5B00"},
    STALE:      {bg: "rgba(241,196,15,0.20)",  fg: "#8A5B00"},
    NEVER_SEEN: {bg: "rgba(123,130,145,0.16)", fg: Grey6},
    REVOKED:    {bg: "rgba(231,76,60,0.14)",   fg: "#D64535"},
    // Work progress -- project plan phases and milestones.
    DONE:           {bg: "rgba(46,204,113,0.16)", fg: "#1E9E5A"},
    "IN PROGRESS":  {bg: "rgba(20,152,216,0.14)", fg: Blue3},
    // CHANGE ORDER STATES. t1 only, and deliberately so: t5 has no change orders, so adding these
    // to its copy would be a definition of something that cannot appear there. DRAFT and APPROVED
    // are already above and mean here what they mean everywhere.
    //   IN_REVIEW  amber -- live and WANTING ATTENTION: somebody has to sign before it moves.
    //   CLOSED     grey  -- finished and filed. Not green: green is "this is the live one", and a
    //                       closed change order is inert -- what is live is the records it released.
    //   VOIDED     red   -- abandoned. Same weight as VOID, which is the word a revision uses for it.
    IN_REVIEW:      {bg: "rgba(241,196,15,0.20)", fg: "#8A5B00"},
    CLOSED:         {bg: "rgba(123,130,145,0.16)", fg: Grey6},
    // PROJECT CONTAINERS, which have exactly two states and reuse CLOSED above for the second.
    //   OPEN   green -- "this is a live one", the same meaning RELEASED carries for a record, and
    //                   the reason it takes the same green rather than a colour of its own.
    // WITHOUT THIS ROW AN OPEN PROJECT RENDERED GREY -- identical to a closed one. An unknown
    // status falls back to grey rather than raising, which is the right failure for a status
    // nobody has defined and the wrong one for a status the product ships: the pill looked
    // deliberate and said the opposite of the truth. Found while fixing the same class of fault
    // on the card's chip.
    OPEN:           {bg: "rgba(46,204,113,0.16)",  fg: "#1E9E5A"},
    VOIDED:         {bg: "rgba(231,76,60,0.14)",   fg: "#D64535"},
    APPROVED:       {bg: "rgba(46,204,113,0.16)",  fg: "#1E9E5A"},
    // PENDING_CHANGE is BLUE, the same blue as DRAFT, and that is the honest answer rather than a
    // shortage of colours. A record in a draft change order is still being worked on -- it is still
    // editable, by the same people -- so painting it amber would tell a reader somebody is waiting
    // on them when nobody is. What changed is that the record can no longer be cancelled or voided,
    // and that is not a thing a colour can say.
    PENDING_CHANGE: {bg: "rgba(20,152,216,0.14)",  fg: Blue3},
    // IMPLEMENTATION is amber for the opposite reason: it means signed, and WORK OUTSTANDING. The
    // signatures are all in and the package is waiting on somebody to finish a task -- which is
    // precisely "live and wanting attention", the same thing IN_REVIEW means to an approver.
    IMPLEMENTATION: {bg: "rgba(241,196,15,0.20)", fg: "#8A5B00"},
};

const UNKNOWN = {bg: "rgba(123,130,145,0.16)", fg: Grey6};

export function statusColours(status?: string | null) {
    return (status && PALETTE[status]) || UNKNOWN;
}

/** The status as a coloured pill, or an em dash when there is no status. */
/**
 * Just the colour, for somewhere a whole chip will not fit.
 *
 * Exported so a caller reads the SAME palette rather than copying three hex values out of it --
 * the copy is what drifts when a status is added, and then two surfaces disagree about what
 * DEPRECATED looks like.
 *
 * THE FILL HUE, NOT THE TEXT COLOUR, and the difference is visible. A chip is a pale tint of the
 * hue with a DARKENED version of it for the label -- so RELEASED reads as light green on screen
 * while its `fg` is a dark forest green. A dot painted `fg` is from the right palette entry and
 * still looks like a different colour from the chip beside it, which is exactly the complaint.
 * Taking the hue out of the tint gives a dot that matches what the chip looks like.
 */
export function statusColor(status?: string | null): string {
    if (!status) return Grey6;
    const entry = PALETTE[String(status).toUpperCase()];
    if (!entry) return Grey6;

    // "rgba(46,204,113,0.16)" -> "rgb(46,204,113)". Anything not in that form falls back to the
    // label colour, which is still the right family.
    const match = entry.bg.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    return match ? `rgb(${match[1]}, ${match[2]}, ${match[3]})` : entry.fg;
}

export function statusPill(status?: string | null) {
    if (!status) return <Typography style={{fontSize: 14, color: Grey6}}>—</Typography>;

    const c = statusColours(status);
    // THE STORED VALUE IS NOT THE LABEL. Statuses are stored as codes, and some of them carry an
    // underscore -- IN_REVIEW, NEVER_SEEN. Printing the code puts a piece of the database on screen;
    // the palette is keyed on the code, so only the text shown is changed here.
    const label = String(status).replace(/_/g, " ");
    return <Chip label={label} size="small"
                 sx={{fontSize: 11, height: 22, minWidth: 100, fontWeight: 600,
                      backgroundColor: c.bg, color: c.fg, "& .MuiChip-label": {px: 1}}}/>;
}
